import { useQuery } from '@apollo/client';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Button } from '@mui/material';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import ConfirmDialog from '../../../base/components/ConfirmDialog/ConfirmDialog';
import { Flex } from '../../../base/components/Flex';
import { Loader } from '../../../client/components/Loader';
import {
  CompanyQrsDocument,
  CompanyQrsQuery,
  InvalidateCompanyQrDocument,
} from '../../../graphql/graphql-operations';
import { useMutationWithSnackbar } from '../../../hooks/useMutationWithSnackbar';
import useQueryErrorSnackbar from '../../../hooks/useQueryErrorSnackbar';
import { useTranslationPrefix } from '../../../hooks/useTranslationPrefix';
import { TopBar } from '../../components/TopBar';
import CompanyQrCard from './components/CompanyQRCard';
import CompanyQrDialog from './components/CompanyQrDialog';
import EditCompanyQrDialog from './components/EditCompanyQrDialog';

import OndemandVideoRoundedIcon from '@mui/icons-material/OndemandVideoRounded';
import EditCompanyQrVideosDialog from './components/EditCompanyQrVideosDialog';

type QrCode = CompanyQrsQuery['companyQrs'][0];

const CompanyQrsPage = () => {
  const { _t } = useTranslationPrefix('CompanyQrsPage');
  const { companyId } = useParams<{ companyId: string }>();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState<QrCode | null>(null);
  const [videosDialogOpen, setVideosDialogOpen] = useState<boolean>(false);
  const [invalidateConfirmOpen, setInvalidateConfirmOpen] = useState<
    string | null
  >(null);

  const { data, refetch, error, loading } = useQuery(CompanyQrsDocument, {
    variables: {
      input: {
        companyId: companyId,
      },
    },
    fetchPolicy: 'cache-and-network',
  });
  useQueryErrorSnackbar(error, refetch);

  const [invalidate] = useMutationWithSnackbar(
    'CompanyQrCard.invalidateMutation',
    InvalidateCompanyQrDocument,
    {
      onCompleted: () => {
        refetch();
        setInvalidateConfirmOpen(null);
      },
    },
  );

  const handleInvalidateConfirm = async () => {
    if (invalidateConfirmOpen) {
      await invalidate({
        variables: {
          input: {
            id: invalidateConfirmOpen,
          },
        },
      });
    }
  };

  return loading || !data?.companyQrs ? (
    <Loader />
  ) : (
    <>
      <TopBar
        leftSideText={_t('title', 'Playlist QR codes')}
        actions={
          <Flex gap={2}>
            <Button
              id="manage-qr-videos"
              variant="contained"
              onClick={() => setVideosDialogOpen(true)}
              startIcon={<OndemandVideoRoundedIcon />}
            >
              {_t('editVideos', 'Videos')}
            </Button>

            <Button
              id="create-qr-dialog"
              variant="contained"
              onClick={() => setCreateDialogOpen(true)}
              startIcon={<AddRoundedIcon />}
            >
              {_t('createQr', 'Add QR')}
            </Button>
          </Flex>
        }
      />

      <Flex gap={2} flexDirection="column">
        {data?.companyQrs?.map((qr) => (
          <CompanyQrCard
            key={qr.id}
            qrCode={qr}
            onInvalidateClick={() => setInvalidateConfirmOpen(qr.id)}
            onEditClick={() => setEditDialogOpen(qr)}
          />
        ))}
      </Flex>
      <CompanyQrDialog
        open={createDialogOpen}
        companyId={companyId!}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          refetch();
          setCreateDialogOpen(false);
        }}
      />
      {editDialogOpen && (
        <EditCompanyQrDialog
          open={!!editDialogOpen}
          qrCode={editDialogOpen}
          onClose={() => setEditDialogOpen(null)}
          onSuccess={() => {
            refetch();
            setEditDialogOpen(null);
          }}
        />
      )}
      <ConfirmDialog
        title={_t('invalidateConfirm', 'Invalidate QR code?')}
        open={!!invalidateConfirmOpen}
        onClose={() => setInvalidateConfirmOpen(null)}
        onConfirm={handleInvalidateConfirm}
        confirmButtonColor="error"
        confirmLabel={_t('invalidate', 'Invalidate')}
        confirmButtonStartIcon={<DeleteForeverIcon />}
      />
      <EditCompanyQrVideosDialog
        companyId={companyId!}
        onClose={() => setVideosDialogOpen(false)}
        open={videosDialogOpen}
      />
    </>
  );
};

export default CompanyQrsPage;
